import React, { useMemo } from 'react';
import { useContracts, useConvenios } from '../../contexts/DataContext';
import SearchableListbox from '../../components/SearchableListbox';

// Shared scope for the convenio reportes. Every one of them answers the same
// question twice — across the whole cartera de convenios, or inside one — so
// the grouping and the picker live here instead of in four copies.

export const TODOS = 'all';

export const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(
    Number(n) || 0
  );

export const montoContrato = (contract) => Number(contract?.montoContrato?.value) || 0;

const indexById = (arr) => {
  const map = {};
  (arr || []).forEach((item) => {
    if (item?.id) map[item.id] = item;
  });
  return map;
};

/**
 * Groups every convenio contract the org can see by convenio, each group
 * carrying the beneficiarios/proyectos maps that convenioCompliance needs.
 *
 * @param {string} convenioId - a convenio id, or TODOS for the whole cartera.
 */
export function useConvenioScope(convenioId = TODOS) {
  const { contractsList, contractsById, contractsLoading } = useContracts();
  const { conveniosById, conveniosLoading } = useConvenios();

  const convenioContracts = useMemo(
    () => contractsList.filter((c) => c.convenioId),
    [contractsList]
  );

  const grupos = useMemo(() => {
    const map = new Map();
    convenioContracts.forEach((contract) => {
      const id = contract.convenioId;
      if (!map.has(id)) {
        const convenio = conveniosById?.[id];
        map.set(id, {
          id,
          nombre: convenio?.nombre || 'Convenio sin nombre',
          convenio: convenio || null,
          beneficiariosById: indexById(convenio?.beneficiarios),
          proyectosById: indexById(convenio?.proyectos),
          contracts: [],
        });
      }
      map.get(id).contracts.push(contract);
    });
    return Array.from(map.values()).sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [convenioContracts, conveniosById]);

  const scoped = useMemo(
    () => (convenioId === TODOS ? grupos : grupos.filter((g) => g.id === convenioId)),
    [grupos, convenioId]
  );

  // convenioCompliance takes one pair of maps for the whole contract set, so
  // the cross-convenio mode hands it the union. Beneficiario and proyecto ids
  // are generated per convenio, so a collision would need the same id twice.
  const { beneficiariosById, proyectosById } = useMemo(() => {
    const bens = {};
    const proys = {};
    scoped.forEach((g) => {
      Object.assign(bens, g.beneficiariosById);
      Object.assign(proys, g.proyectosById);
    });
    return { beneficiariosById: bens, proyectosById: proys };
  }, [scoped]);

  const scopedContracts = useMemo(() => scoped.flatMap((g) => g.contracts), [scoped]);

  const options = useMemo(
    () => [
      { value: TODOS, label: 'Todos los convenios' },
      ...grupos.map((g) => ({ value: g.id, label: g.nombre })),
    ],
    [grupos]
  );

  // The convenio a contract belongs to, for reports that aggregate across
  // convenios and still need to name the origin of each row.
  const convenioDeContrato = useMemo(() => {
    const map = new Map();
    scoped.forEach((g) => g.contracts.forEach((c) => map.set(c.id, g)));
    return map;
  }, [scoped]);

  return {
    options,
    grupos: scoped,
    todosLosGrupos: grupos,
    scopedContracts,
    contractsById,
    convenioDeContrato,
    beneficiariosById,
    proyectosById,
    isAll: convenioId === TODOS,
    loading: contractsLoading || conveniosLoading,
  };
}

export function ConvenioPicker({ value, onChange, options }) {
  return (
    <SearchableListbox
      value={value}
      onChange={onChange}
      options={options}
      className="min-w-[260px]"
      placeholder="Todos los convenios"
      searchPlaceholder="Buscar convenio..."
      popupClassName="w-[300px]"
      aria-label="Convenio"
    />
  );
}

// `min-w-0` es lo que evita que un importe largo desborde: sin él la pista del
// grid toma como mínimo el ancho de min-content, y un "$23,333,883" no tiene
// dónde partirse, así que empuja la celda más allá del contenedor.
export function StatTile({ label, value, sub, alert = false }) {
  return (
    <div
      className={`min-w-0 rounded-lg border bg-white p-3 ${
        alert ? 'border-red-200' : 'border-azul-surelia/20'
      }`}
    >
      <div className="truncate text-[10px] uppercase tracking-wide text-gray-500" title={label}>
        {label}
      </div>
      <div
        className={`truncate text-lg font-bold tabular-nums sm:text-xl ${
          alert ? 'text-red-600' : 'text-azul-surelia'
        }`}
        title={String(value)}
      >
        {value}
      </div>
      {sub && (
        <div className="mt-0.5 truncate text-[11px] text-gray-500" title={String(sub)}>
          {sub}
        </div>
      )}
    </div>
  );
}

export function EmptyScope({ children }) {
  return <div className="py-10 text-center text-sm text-zinc-500">{children}</div>;
}
